import { SelectableTreeMenuItem } from "@biggeo/bg-ui/lab";
import { bgLongToNumber, toNonReadonlyArray } from "@biggeo/bg-utils";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import {
    centroid,
    difference,
    featureCollection,
    intersect,
    polygon,
    union,
} from "@turf/turf";
import * as A from "fp-ts/lib/Array";
import * as O from "fp-ts/lib/Option";
import { pipe } from "fp-ts/lib/function";
import concat from "lodash/concat";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";

import {
    AreaExtended,
    SavedArea,
    SubscriptionResponse,
} from "@biggeo/bg-server-lib/studio";

import { getShapeProperties } from "./geometry-utils";
import { FunctionType } from "./type";
import { isPolygonFeature, isPolygonFeatureCollection } from "./converters";
import {
    MapContextPolygon,
    MapContextPolygonSource,
} from "../mapbox/context/types";

export const sumCounts = <T extends { count: number }>(d: T[]) =>
    d.reduce((a, c) => a + c.count, 0);

export const isNonPointsLengthPositive = (
    data: SubscriptionResponse[]
): boolean => {
    return data.some((result: SubscriptionResponse) =>
        result.geometry ? !isEmpty(result.geometry.nonPoints) : false
    );
};
export const isNonPointCountPositive = (
    data: SubscriptionResponse[]
): boolean => {
    return data.some((result: SubscriptionResponse) =>
        result.geometry
            ? bgLongToNumber(result.geometry.nonPointCount) > 0
            : false
    );
};

export const isAggregationNonEmpty = (
    data: SubscriptionResponse[]
): boolean => {
    return data.some(
        (result: SubscriptionResponse) => !isEmpty(result.geometry?.aggregation)
    );
};

export const updateArray = <
    T extends {
        readonly id?: string | number;
    },
>(
    b: T,
    a: Array<T>
): Array<T> => {
    return pipe(
        a,
        A.findFirst((x) => isEqual(x.id, b.id)),
        O.fold(
            () => concat(a, b),
            (e) => a.map((i) => (isEqual(i.id, e.id) ? { ...i, ...b } : i))
        )
    );
};

export const updateSelectedById = <
    T extends {
        readonly id?: string | number | undefined;
    },
>(
    b: T,
    a: Array<T>
): Array<T> => {
    return pipe(
        a,
        A.findFirst((x) => isEqual(x.id, b.id)),
        O.fold(
            () => concat(a, b),
            () => a.filter((v) => !isEqual(v.id, b.id))
        )
    );
};

export const updateItem = (
    item: SelectableTreeMenuItem,
    selected: boolean
): SelectableTreeMenuItem => {
    return {
        key: String(item.id),
        id: item.id,
        title: item.title,
        selected: selected,
        subItems: pipe(
            item.subItems,
            toNonReadonlyArray,
            A.map((sub) => updateItem(sub, selected))
        ),
    };
};

export const convertSlimSavedAreas = (
    savedAreas: readonly AreaExtended[],
    selectedId?: string
): SelectableTreeMenuItem[] => {
    return pipe(
        savedAreas,
        toNonReadonlyArray,
        A.map((area) => ({
            key: String(area.area.id),
            id: area.area.id,
            selected: false,
            hideCheckBox: !isEmpty(area.savedAreaGroups),
            title: area.area.name.trim(),
            subItems: [
                ...pipe(
                    area.savedAreas,
                    toNonReadonlyArray,
                    A.filter((sa) => Boolean(sa.mapUse)),
                    A.map(
                        (sa): SelectableTreeMenuItem => ({
                            key: sa.id,
                            id: sa.id,
                            title: sa.name.trim(),
                            selected: selectedId === sa.id,
                        })
                    )
                ),
                ...pipe(
                    area.savedAreaGroups,
                    toNonReadonlyArray,
                    A.filter(
                        (sag) =>
                            !isEmpty(sag.subSavedAreaGroups) ||
                            !isEmpty(sag.savedAreas)
                    ),
                    A.map(
                        (sag): SelectableTreeMenuItem => ({
                            key: String(`sag-${sag.savedAreaGroup.id}`),
                            id: sag.savedAreaGroup.id,
                            title: sag.savedAreaGroup.name.trim(),
                            selected: false,
                            hideCheckBox: true,
                            subItems: pipe(
                                sag.savedAreas,
                                toNonReadonlyArray,
                                A.filter((sa) => Boolean(sa.mapUse)),
                                A.map(
                                    (sa): SelectableTreeMenuItem => ({
                                        key: String(sa.id),
                                        id: sa.id,
                                        title: sa.name.trim(),
                                        selected: selectedId === sa.id,
                                    })
                                )
                            ),
                        })
                    )
                ),
            ],
        }))
    );
};

export const getConflictAreas = ({
    shape,
    boundaries,
}: {
    boundaries: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>[];
    shape?: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>;
}): {
    intersection?: GeoJSON.Feature<
        GeoJSON.Geometry,
        GeoJSON.GeoJsonProperties
    > | null;
    difference?: GeoJSON.Feature<
        GeoJSON.MultiPolygon | GeoJSON.Polygon,
        GeoJSON.GeoJsonProperties
    > | null;
    shape?: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>;
} => {
    if (shape && !isPolygonFeature(shape)) {
        return {
            intersection: null,
            difference: null,
            shape,
        };
    }

    // TODO(Andrew) I am unsure of how to handle if polygons is empty and here we just choose polygons[0]
    // Could we union with an empty polygon (this should give the first) or union with polygon[1] thereafter
    // I think unioning with your self can be numerically unstable, but unsure (?)
    // This should be with the function above if it's always used together?

    // I feel like this should be possible but something about the geometries not matching polygons (types stuff that I don't understand in TSX)
    //const { savedAreaPolygons } = getOutsideArea(savedAreas); // I feel like
    //const unionPolygon = savedAreaPolygons.reduce((acc, polygon) => {
    //    const output = union(featureCollection([acc, polygon]));

    //    return output ? output : acc;
    //}, savedAreaPolygons[0]);

    // This should be put somewhere else
    // getSavedAreaPolygon but this somewhat in getPolygonsFromSavedAreas but not quite the same?

    const unionPolygon = boundaries.reduce((acc, polygon) => {
        const collection = featureCollection([acc, polygon]);
        const output = isPolygonFeatureCollection(collection)
            ? union(collection)
            : undefined;

        return output ? output : acc;
    }, boundaries[0]);

    // Algorithm:
    // unionPolygon : all saved shape boundaries unionioned together
    // differece(shape, unionPolygon) -> gives the region of the shape (1st arg) that lies outside of the unionPolygon (2nd arg)
    // intersect(shape, unionPolygon) -> gives the region of the shape that lies inside of the unionPolygon
    const collection = shape
        ? featureCollection([shape, unionPolygon])
        : undefined;

    const intersection =
        collection && isPolygonFeatureCollection(collection)
            ? difference(collection)
            : undefined;
    const differenceInShape =
        collection && isPolygonFeatureCollection(collection)
            ? intersect(collection)
            : undefined;

    return {
        intersection,
        difference: differenceInShape,
        shape,
    };
};

export const getSavedViewPolygons = (
    savedArea: SavedArea
): {
    drawnPolygons: MapContextPolygon[];
    boundaryPolygons: MapContextPolygon[];
} => {
    const polygons = pipe(
        savedArea.geometries.geometries,
        toNonReadonlyArray,
        A.mapWithIndex((index, geometry) => {
            const isBoundary = !!geometry.properties?.fkSavedAreaId;

            const id = isBoundary
                ? geometry.properties?.fkSavedAreaId
                : (geometry.id ?? `saved-view-${index}`);
            const coordinates = geometry.coordinates as GeoJSON.Position[][];
            const isSquare = coordinates[0].length === 5;
            const isCircle = coordinates[0].length >= 65;

            const circle = isCircle ? polygon([coordinates[0]]) : undefined;
            const center = circle ? centroid(circle) : undefined;

            const feature = {
                id,
                type: MapboxDraw.constants.geojsonTypes.FEATURE,
                geometry: {
                    type: MapboxDraw.constants.geojsonTypes.POLYGON,
                    coordinates: coordinates,
                },
                properties: {
                    ...getShapeProperties(geometry.properties || undefined),
                    isSquare,
                    isCircle,
                    featureId: id,
                    type: isSquare
                        ? FunctionType.square
                        : isCircle
                          ? FunctionType.radius
                          : FunctionType.polygon,
                    center: center ? center.geometry.coordinates : undefined,
                },
            };

            return {
                id,
                feature,
                source: isBoundary
                    ? MapContextPolygonSource.boundary
                    : MapContextPolygonSource.drawnShape,
                isSelected: false,
            };
        })
    );

    return {
        drawnPolygons: polygons.filter(
            (p) => p.source === MapContextPolygonSource.drawnShape
        ),
        boundaryPolygons: polygons.filter(
            (p) => p.source === MapContextPolygonSource.boundary
        ),
    };
};
